import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

/**
 * A tiny history router scoped to a base path, so the builder can live at "/"
 * in the operator app and under any prefix inside another host without
 * depending on that host's router.
 */
type Nav = { path: string; go: (to: string) => void; base: string };
const NavContext = createContext<Nav>({ path: "/", go: () => {}, base: "" });

const strip = (base: string, p: string) => (p.startsWith(base) ? p.slice(base.length) || "/" : "/");

export function NavProvider({ base, children }: { base: string; children: ReactNode }) {
  const [path, setPath] = useState(() => strip(base, window.location.pathname));
  useEffect(() => {
    const on = () => setPath(strip(base, window.location.pathname));
    window.addEventListener("popstate", on);
    return () => window.removeEventListener("popstate", on);
  }, [base]);
  const go = useCallback(
    (to: string) => {
      window.history.pushState(null, "", `${base}${to}`);
      setPath(to);
      window.scrollTo(0, 0);
    },
    [base],
  );
  return <NavContext.Provider value={{ path, go, base }}>{children}</NavContext.Provider>;
}

export const useNav = () => useContext(NavContext);

export function Link({ to, className, children }: { to: string; className?: string; children: ReactNode }) {
  const { go, base } = useNav();
  return (
    <a
      href={`${base}${to}`}
      className={className}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        go(to);
      }}
    >
      {children}
    </a>
  );
}

/** Polls `load` every `ms` while `active` is true; returns [data, reload, error]. */
export function usePolling<T>(load: () => Promise<T>, deps: unknown[], opts: { ms?: number; active?: (d: T | undefined) => boolean } = {}) {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  const reload = useCallback(async () => {
    try {
      setData(await load());
      setError(undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    void reload();
  }, [reload]);
  useEffect(() => {
    const ms = opts.ms ?? 2500;
    const t = setInterval(() => {
      if (!opts.active || opts.active(data)) void reload();
    }, ms);
    return () => clearInterval(t);
  }, [reload, data, opts.ms, opts.active]);
  return [data, reload, error] as const;
}
